import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { 
  Users, Star, Award, MapPin, Clock, CheckCircle, X, Search, Filter,
  ChevronRight, RefreshCw, UserCheck, AlertCircle, TrendingUp, Briefcase,
  Target, Zap, Heart, Send, Eye, Calendar, DollarSign, Shield
} from 'lucide-react';

export default function WorkerMatching({ job, onClose, onWorkerSelected }) {
  const [loading, setLoading] = useState(true);
  const [matchedWorkers, setMatchedWorkers] = useState([]);
  const [preferredWorkers, setPreferredWorkers] = useState([]);
  const [filters, setFilters] = useState({
    min_rating: 0,
    min_experience: '',
    availability: 'all',
    has_certifications: false,
    distance_km: 50
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [inviting, setInviting] = useState(null);
  const [viewingWorker, setViewingWorker] = useState(null);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [expandedBreakdown, setExpandedBreakdown] = useState(null);

  useEffect(() => {
    if (job) {
      loadMatchedWorkers();
      loadPreferredWorkers();
    }
  }, [job]);

  const loadMatchedWorkers = async () => {
    setLoading(true);
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/matches`, {
        method: 'POST',
        body: JSON.stringify(filters)
      });
      setMatchedWorkers(response.workers || []);
    } catch (error) {
      console.error('Failed to load matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferredWorkers = async () => {
    try {
      const response = await apiCall('/api/employer/preferred-workers');
      setPreferredWorkers(response.workers || []);
    } catch (error) {
      console.error('Failed to load preferred workers:', error);
    }
  };

  const handleInviteWorker = async (workerId) => {
    setInviting(workerId);
    try {
      await apiCall(`/api/employer/jobs/${job._id}/invite`, {
        method: 'POST',
        body: JSON.stringify({ worker_id: workerId })
      });
      toast.success('Invitation sent!');
      onWorkerSelected?.(workerId);
    } catch (error) {
      toast.error(error.message || 'Failed to invite worker');
    } finally {
      setInviting(null);
    }
  };

  const handleAddToPreferred = async (worker) => {
    try {
      await apiCall('/api/employer/preferred-workers', {
        method: 'POST',
        body: JSON.stringify({ worker_id: worker._id })
      });
      toast.success(`${worker.full_name} added to preferred workers!`);
      loadPreferredWorkers();
    } catch (error) {
      toast.error(error.message || 'Failed to add to preferred');
    }
  };

  const handleViewProfile = async (worker) => {
    setViewingWorker(worker);
    setLoadingProfile(true);
    try {
      const response = await apiCall(`/api/workers/${worker._id}/profile`);
      setWorkerProfile(response.worker || response);
    } catch (error) {
      // Use the worker data we already have
      setWorkerProfile(worker);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleBulkInvite = async () => {
    if (selectedWorkers.length === 0) {
      toast.error('Please select workers to invite');
      return;
    }

    setLoading(true);
    try {
      await apiCall(`/api/employer/jobs/${job._id}/bulk-invite`, {
        method: 'POST',
        body: JSON.stringify({ worker_ids: selectedWorkers })
      });
      toast.success(`Invited ${selectedWorkers.length} workers!`);
      setSelectedWorkers([]);
    } catch (error) {
      toast.error(error.message || 'Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoFill = async () => {
    setAutoFilling(true);
    try {
      const res = await apiCall(`/api/employer/jobs/${job._id}/auto-fill`, { method: 'POST' });
      toast.success(res.message || 'Shift auto-filled!');
      onWorkerSelected?.(res.worker?.worker_id);
      loadMatchedWorkers();
    } catch (error) {
      toast.error(error.message || 'Failed to auto-fill');
    } finally { setAutoFilling(false); }
  };

  const getScoreBarColor = (pct) => {
    if (pct >= 80) return 'bg-emerald-500';
    if (pct >= 60) return 'bg-blue-500';
    if (pct >= 40) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-100';
    if (score >= 75) return 'text-blue-600 bg-blue-100';
    if (score >= 60) return 'text-amber-600 bg-amber-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getMatchScoreLabel = (score) => {
    if (score >= 90) return 'Excellent Match';
    if (score >= 75) return 'Good Match';
    if (score >= 60) return 'Fair Match';
    return 'Potential Match';
  };

  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const filteredWorkers = matchedWorkers.filter(worker => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        worker.full_name?.toLowerCase().includes(query) ||
        worker.skills?.some(s => s.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Target className="w-8 h-8" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold">Worker Matching</h2>
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-[10px] font-bold uppercase tracking-wider">AI Powered</span>
                </div>
                <p className="text-white/80">{job?.title} • {matchedWorkers.length} matches found</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAutoFill}
                disabled={autoFilling}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl font-medium transition flex items-center gap-2 text-sm"
                data-testid="auto-fill-btn"
              >
                {autoFilling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Auto-Fill Shift
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex gap-4 mt-4">
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Total Matches</span>
              <p className="text-xl font-bold">{matchedWorkers.length}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Excellent Matches</span>
              <p className="text-xl font-bold">{matchedWorkers.filter(w => w.match_score >= 80).length}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Strong Matches</span>
              <p className="text-xl font-bold">{matchedWorkers.filter(w => w.match_score >= 60 && w.match_score < 80).length}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Preferred Workers</span>
              <p className="text-xl font-bold">{preferredWorkers.length}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters Bar */}
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workers by name or skills..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2.5 rounded-xl font-medium transition flex items-center gap-2 ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>

            <button
              onClick={loadMatchedWorkers}
              disabled={loading}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {selectedWorkers.length > 0 && (
              <button
                onClick={handleBulkInvite}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg transition flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Invite Selected ({selectedWorkers.length})
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Rating</label>
                  <select
                    value={filters.min_rating}
                    onChange={(e) => setFilters({ ...filters, min_rating: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="0">Any Rating</option>
                    <option value="3">3+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="4.5">4.5+ Stars</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                  <select
                    value={filters.min_experience}
                    onChange={(e) => setFilters({ ...filters, min_experience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Any Experience</option>
                    <option value="entry">Entry Level</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="experienced">Experienced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Distance</label>
                  <select
                    value={filters.distance_km}
                    onChange={(e) => setFilters({ ...filters, distance_km: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="10">Within 10 km</option>
                    <option value="25">Within 25 km</option>
                    <option value="50">Within 50 km</option>
                    <option value="100">Within 100 km</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.has_certifications}
                      onChange={(e) => setFilters({ ...filters, has_certifications: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Has Required Certifications</span>
                  </label>
                </div>
              </div>
              <button
                onClick={loadMatchedWorkers}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Apply Filters
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[55vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Matches Found</h3>
              <p className="text-gray-500">Try adjusting your filters or wait for more workers to become available.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredWorkers.map((worker) => (
                <div 
                  key={worker._id}
                  className={`bg-white rounded-xl border-2 p-5 transition hover:shadow-md ${
                    selectedWorkers.includes(worker._id)
                      ? 'border-blue-500 bg-blue-50/30'
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Selection Checkbox */}
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={selectedWorkers.includes(worker._id)}
                        onChange={() => toggleWorkerSelection(worker._id)}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600"
                      />
                    </div>

                    {/* Worker Avatar */}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center overflow-hidden">
                        {worker.profile_photo ? (
                          <img src={worker.profile_photo} alt={worker.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl font-bold text-white">
                            {worker.full_name?.charAt(0)?.toUpperCase() || 'W'}
                          </span>
                        )}
                      </div>
                      {worker.is_preferred && (
                        <div className="absolute -top-1 -right-1 p-1 bg-pink-500 rounded-full">
                          <Heart className="w-3 h-3 text-white fill-white" />
                        </div>
                      )}
                    </div>

                    {/* Worker Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{worker.full_name}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                              {worker.rating?.toFixed(1) || 'New'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {worker.jobs_completed || 0} jobs
                            </span>
                            {worker.distance_km && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {worker.distance_km.toFixed(1)} km
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Match Score */}
                        <button
                          onClick={() => setExpandedBreakdown(expandedBreakdown === worker._id ? null : worker._id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer transition hover:ring-2 hover:ring-offset-1 ${getMatchScoreColor(worker.match_score || 0)}`}
                          data-testid={`match-score-${worker._id}`}
                        >
                          <div className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {worker.match_score || 0}%
                          </div>
                          <p className="text-xs font-normal">{getMatchScoreLabel(worker.match_score || 0)}</p>
                        </button>
                      </div>

                      {/* Skills */}
                      {worker.skills && worker.skills.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {worker.skills.slice(0, 5).map((skill, idx) => (
                            <span 
                              key={idx}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                job?.required_skills?.includes(skill)
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {skill}
                            </span>
                          ))}
                          {worker.skills.length > 5 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                              +{worker.skills.length - 5} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Certifications */}
                      {worker.certifications && worker.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {worker.certifications.slice(0, 3).map((cert, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium flex items-center gap-1"
                            >
                              <Shield className="w-3 h-3" />
                              {cert}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Match Indicators */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {worker.has_all_certifications && (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            All Certifications
                          </span>
                        )}
                        {worker.available_for_date && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Available
                          </span>
                        )}
                        {worker.worked_with_before && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Worked Before
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleInviteWorker(worker._id)}
                        disabled={inviting === worker._id}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
                      >
                        {inviting === worker._id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Invite
                      </button>
                      {!worker.is_preferred && (
                        <button
                          onClick={() => handleAddToPreferred(worker)}
                          className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg font-medium hover:bg-pink-200 transition flex items-center gap-2"
                        >
                          <Heart className="w-4 h-4" />
                          Favorite
                        </button>
                      )}
                      <button
                        onClick={() => handleViewProfile(worker)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition flex items-center gap-2"
                        data-testid="view-profile-btn"
                      >
                        <Eye className="w-4 h-4" />
                        Profile
                      </button>
                    </div>
                  </div>

                  {/* Conflict Warning */}
                  {worker.has_conflict && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <span className="text-sm text-amber-700">
                        This worker has another job scheduled during this time
                      </span>
                    </div>
                  )}

                  {/* AI Score Breakdown */}
                  {expandedBreakdown === worker._id && worker.score_breakdown && (
                    <div className="mt-3 p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid={`score-breakdown-${worker._id}`}>
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5 text-indigo-500" /> AI Match Breakdown
                      </p>
                      <div className="space-y-2.5">
                        {[
                          { label: 'Skills Match', score: worker.score_breakdown.skills, max: worker.score_breakdown.skills_max },
                          { label: 'Certifications', score: worker.score_breakdown.certifications, max: worker.score_breakdown.certifications_max },
                          { label: 'Reliability', score: worker.score_breakdown.reliability, max: worker.score_breakdown.reliability_max },
                          { label: 'Availability', score: worker.score_breakdown.availability, max: worker.score_breakdown.availability_max },
                          { label: 'Performance', score: worker.score_breakdown.performance, max: worker.score_breakdown.performance_max },
                        ].map(item => {
                          const pct = item.max > 0 ? (item.score / item.max) * 100 : 0;
                          return (
                            <div key={item.label} className="flex items-center gap-3">
                              <span className="text-xs text-slate-600 w-24 flex-shrink-0">{item.label}</span>
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-500 ${getScoreBarColor(pct)}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-slate-700 w-14 text-right">{item.score}/{item.max}</span>
                            </div>
                          );
                        })}
                      </div>
                      {worker.past_jobs_with_employer > 0 && (
                        <p className="text-[10px] text-indigo-600 mt-2 font-medium">{worker.past_jobs_with_employer} past job{worker.past_jobs_with_employer > 1 ? 's' : ''} with you</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Worker Profile Modal */}
      {viewingWorker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" data-testid="worker-profile-modal">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-3xl font-bold shadow-lg">
                    {viewingWorker.profile_photo ? (
                      <img src={viewingWorker.profile_photo} alt={viewingWorker.full_name} className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      viewingWorker.full_name?.charAt(0) || 'W'
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{viewingWorker.full_name}</h2>
                    <div className="flex items-center gap-3 mt-1">
                      {viewingWorker.rating > 0 && (
                        <span className="flex items-center gap-1 text-amber-400">
                          <Star className="w-4 h-4 fill-current" />
                          {viewingWorker.rating?.toFixed(1)}
                        </span>
                      )}
                      <span className="text-slate-300">{viewingWorker.completed_jobs || 0} jobs completed</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => { setViewingWorker(null); setWorkerProfile(null); }}
                  className="p-2 hover:bg-white/20 rounded-xl transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[55vh]">
              {loadingProfile ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Match Score */}
                  {viewingWorker.match_score && (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-xl font-bold ${
                        viewingWorker.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        viewingWorker.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {viewingWorker.match_score}%
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Match Score</h4>
                        <p className="text-sm text-slate-500">
                          {viewingWorker.match_score >= 80 ? 'Excellent match for this job' :
                           viewingWorker.match_score >= 60 ? 'Good potential match' :
                           'Partial match - review skills'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {(workerProfile?.bio || viewingWorker.bio) && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">About</h4>
                      <p className="text-slate-600">{workerProfile?.bio || viewingWorker.bio}</p>
                    </div>
                  )}

                  {/* Skills */}
                  {(workerProfile?.skills || viewingWorker.skills)?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {(workerProfile?.skills || viewingWorker.skills).map((skill, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {(workerProfile?.certifications || viewingWorker.certifications)?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Certifications</h4>
                      <div className="flex flex-wrap gap-2">
                        {(workerProfile?.certifications || viewingWorker.certifications).map((cert, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium flex items-center gap-1">
                            <Award className="w-4 h-4" />
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <Briefcase className="w-4 h-4" />
                        <span className="text-sm">Experience</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {workerProfile?.experience_years || viewingWorker.experience_years || 0} years
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-sm">Hourly Rate</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        ${workerProfile?.hourly_rate || viewingWorker.hourly_rate || 'Not specified'}/hr
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">Location</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {workerProfile?.location || viewingWorker.location || 'Not specified'}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">Availability</span>
                      </div>
                      <p className="font-semibold text-slate-800">
                        {(workerProfile?.availability || viewingWorker.availability) === 'available' ? 'Available Now' : 
                         (workerProfile?.availability || viewingWorker.availability) === 'busy' ? 'Currently Busy' : 'Flexible'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <button
                onClick={() => { setViewingWorker(null); setWorkerProfile(null); }}
                className="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition"
              >
                Close
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { handleAddToPreferred(viewingWorker); setViewingWorker(null); }}
                  className="px-5 py-2.5 bg-pink-100 text-pink-700 font-medium rounded-xl hover:bg-pink-200 transition flex items-center gap-2"
                >
                  <Heart className="w-4 h-4" />
                  Add to Favorites
                </button>
                <button
                  onClick={() => { handleInviteWorker(viewingWorker._id); setViewingWorker(null); }}
                  disabled={inviting === viewingWorker._id}
                  className="px-5 py-2.5 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Invite to Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
